import React, { useCallback } from 'react';

import { Typography } from 'antd';
import styled from 'styled-components';

import {
  COMPANY_PRICING_SELECTOR_DESCRIPTION,
  DEFAULT_SELECTOR_DESCRIPTION,
  FURTHER_PRICING_SELECTOR_DESCRIPTION,
} from './constants';
import ShopRequestModalBaseCommunitySelect from './ShopRequestModalBaseCommunitySelect';
import OptionsCheckboxes from './ShopRequestModalCheckboxes';
import { useShopRequestModalContext } from './ShopRequestModalContext';
import { MYSTERY_SHOP_MODAL_REQUEST_FROM, SHOP_REQUEST_TYPE } from '../constants';

const Container = styled.div`
  display: flex;
  flex-flow: column;
`;

const StyledLabel = styled(Typography.Text)`
  padding: 20px 0 20px 30px;
  color: var(--text-secondary);
  font-size: 14px;
`;

const CommunitySelector = styled.div`
  display: flex;
  flex-flow: row;
  border-bottom: solid 1px var(--line-gray);
  padding: 0 0 9px 30px;
`;

const Title = styled.div`
  font-size: 12px;
  font-family: var(--font-bold);
  letter-spacing: 0.5px;
  width: 480px;
`;

const HeaderOptions = styled.div`
  margin-left: 30px;
`;

const ShopRequestCommunitiesSelector = () => {
  const { shopRequests, requestFrom, requestType, setShopRequests } = useShopRequestModalContext();
  const isPricingRequest = requestType === SHOP_REQUEST_TYPE.MYSTERY_SHOP;
  let selectorDescription = DEFAULT_SELECTOR_DESCRIPTION;

  if (isPricingRequest) {
    selectorDescription =
      requestFrom === MYSTERY_SHOP_MODAL_REQUEST_FROM.FURTHER
        ? FURTHER_PRICING_SELECTOR_DESCRIPTION
        : COMPANY_PRICING_SELECTOR_DESCRIPTION;
  }

  const allIndependentLivingEnabled =
    shopRequests.filter((item: any) => item.independentLiving).length === shopRequests.length;
  const allAssistedLivingEnabled =
    shopRequests.filter((item: any) => item.assistedLiving).length === shopRequests.length;
  const allMemoryCareEnabled = shopRequests.filter((item: any) => item.memoryCare).length === shopRequests.length;

  const toggleAllAssistedLiving = useCallback(
    (checked: boolean) => {
      setShopRequests(shopRequests.map((request) => ({ ...request, assistedLiving: checked })));
    },
    [shopRequests, setShopRequests]
  );

  const toggleAllIndependentLiving = useCallback(
    (checked: boolean) => {
      setShopRequests(shopRequests.map((request) => ({ ...request, independentLiving: checked })));
    },
    [shopRequests, setShopRequests]
  );

  const toggleAllMemoryCare = useCallback(
    (checked: boolean) => {
      setShopRequests(shopRequests.map((request) => ({ ...request, memoryCare: checked })));
    },
    [shopRequests, setShopRequests]
  );

  const noCommunitiesSelected = shopRequests.length === 0;

  return (
    <Container>
      <StyledLabel>{selectorDescription}</StyledLabel>
      {requestFrom === MYSTERY_SHOP_MODAL_REQUEST_FROM.COMPANY && <ShopRequestModalBaseCommunitySelect />}
      <CommunitySelector>
        <Title>
          {requestFrom === MYSTERY_SHOP_MODAL_REQUEST_FROM.FURTHER ? 'COMMUNITY' : 'COMPETITORS TO SHOP FOR'}
        </Title>
        {isPricingRequest && (
          <HeaderOptions>
            <OptionsCheckboxes
              independentLiving={allIndependentLivingEnabled && !noCommunitiesSelected}
              assistedLiving={allAssistedLivingEnabled && !noCommunitiesSelected}
              memoryCare={allMemoryCareEnabled && !noCommunitiesSelected}
              disabled={noCommunitiesSelected}
              toggleIndependentLiving={toggleAllIndependentLiving}
              toggleAssistedLiving={toggleAllAssistedLiving}
              toggleMemoryCare={toggleAllMemoryCare}
            />
          </HeaderOptions>
        )}
      </CommunitySelector>
    </Container>
  );
};

export default ShopRequestCommunitiesSelector;
